import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Modal } from "@/components/Modals";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { usePOS } from "@/context/POSContext";
import * as admin from "@/lib/adminApi";
import * as tg from "@/lib/integrationsApi";

/**
 * Staff → Roles (owner, 2026-10-31: "Custom roles instead"). A role is a name on one
 * of the three kinds the till knows — admin, cashier, captain — plus what it adds to
 * and takes away from that kind. Give it to people on their own screen in Staff.
 * Changing a role changes everyone who holds it; a person's own switches still win.
 *
 * The one place for what a role gets by default (owner, 2026-11-01): the kinds'
 * own starting points at the top, then each role's POS permissions, its Telegram
 * items, and whether new staff of its kind start with it.
 */
const KINDS = [
  ["admin", "Admin — runs the outlet"],
  ["cashier", "Cashier — the billing PC"],
  ["captain", "Captain — the captain pad"],
];
const KIND_WORD = { admin: "Admin", cashier: "Cashier", captain: "Captain" };
const GROUPS = [
  ["access", "Screens"], ["floor", "Table & order actions"], ["billing", "Billing"], ["orders", "Orders"],
  ["reports", "Reports"], ["inventory", "Inventory"], ["purchase", "Purchase"], ["register", "Register"], ["attendance", "Attendance"], ["crm", "CRM & khata"],
  ["masters", "Masters"], ["settings", "Settings"], ["printing", "Printing"], ["pages", "Back-office pages"],
  ["system", "System"], ["users", "Staff"],
];
const reason = err => err?.response?.data?.detail || err?.message || "no answer from the server";

/** The permission switches, grouped; `note(key)` says where each value comes from. */
function PermissionGroups({ catalog, on, toggle, note, testPrefix }) {
  // Report switches follow "All reports"; editors show the parents only.
  const shown = catalog.filter(p => !p.parent);
  return GROUPS.map(([group, label]) => {
    const keys = shown.filter(p => p.group === group);
    if (!keys.length) return null;
    return (
      <fieldset key={group} className="border rounded-lg p-snug">
        <legend className="text-meta font-medium text-muted-foreground px-tight">{label}</legend>
        {keys.map(p => {
          const [words, changed] = note(p.key);
          return (
            <label key={p.key} className="flex items-center gap-snug py-hair text-body cursor-pointer">
              <input type="checkbox" className="w-4 h-4" checked={on(p.key)} onChange={() => toggle(p.key)}
                     data-testid={`${testPrefix}-${p.key}`} />
              <span className="flex-1">{p.label}</span>
              <span className={`text-micro ${changed ? "text-info font-medium" : "text-muted-foreground"}`}>{words}</span>
            </label>
          );
        })}
      </fieldset>
    );
  });
}

/** What Telegram gives holders of this role by default (was Telegram → Roles & defaults). */
function TelegramItems({ items, itemsError, value, onChange }) {
  if (itemsError) {
    return <p className="text-meta text-muted-foreground" data-testid="role-telegram-unavailable">Telegram items not shown: {itemsError}</p>;
  }
  if (!items) return <p className="text-meta text-muted-foreground">Loading Telegram items…</p>;
  return (
    <fieldset className="border rounded-lg p-snug" data-testid="role-telegram">
      <legend className="text-meta font-medium text-muted-foreground px-tight">Telegram</legend>
      <p className="text-micro text-muted-foreground pb-tight">
        What holders get in the bot. They still need the permission named under each item, and a person's own
        switch under Telegram → People wins.
      </p>
      {items.map(i => (
        <label key={i.key} className={`flex items-center gap-snug py-hair text-body ${i.built ? "cursor-pointer" : "opacity-60"}`}>
          <input type="checkbox" className="w-4 h-4" checked={Boolean(value[i.key])} disabled={!i.built}
                 onChange={() => onChange({ ...value, [i.key]: !value[i.key] })}
                 data-testid={`role-telegram-${i.key}`} />
          <span className="flex-1">{i.label}</span>
          <span className="text-micro text-muted-foreground">
            {i.built ? `${i.kind === "notification" ? "Message" : "In the chat"} · needs ${i.needs}` : "Coming next"}
          </span>
        </label>
      ))}
    </fieldset>
  );
}

function RoleEditor({ role, catalog, rolePerms, roles, items, itemsError, onClose, onSaved }) {
  const { token } = usePOS();
  const [draft, setDraft] = useState(role
    ? { name: role.name, base_kind: role.base_kind, description: role.description || "", adds: role.adds,
        removes: role.removes, telegram: role.telegram || {}, is_default: Boolean(role.is_default) }
    : { name: "", base_kind: "cashier", description: "", adds: [], removes: [], telegram: {}, is_default: false });
  const [busy, setBusy] = useState(false);
  const [moveTo, setMoveTo] = useState("");
  const base = useMemo(() => new Set(rolePerms[draft.base_kind]?.permissions || []), [rolePerms, draft.base_kind]);
  const on = key => (draft.removes.includes(key) ? false : draft.adds.includes(key) || base.has(key));
  const toggle = key => setDraft(d => {
    const adds = d.adds.filter(k => k !== key);
    const removes = d.removes.filter(k => k !== key);
    if (on(key)) { if (base.has(key)) removes.push(key); } else if (!base.has(key)) adds.push(key);
    return { ...d, adds, removes };
  });
  const note = key => {
    const changed = draft.adds.includes(key) ? "added" : draft.removes.includes(key) ? "removed" : null;
    return [changed || `from ${KIND_WORD[draft.base_kind]}`, Boolean(changed)];
  };
  const currentDefault = roles.find(r => r.base_kind === draft.base_kind && r.is_default && r.id !== role?.id);

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...draft, name: draft.name.trim(), description: draft.description.trim() || null };
      if (role) await admin.updateStaffRole(role.id, body, token); else await admin.createStaffRole(body, token);
      toast.success(role ? `${body.name} saved — everyone who holds it follows` : `${body.name} added`);
      onSaved();
    } catch (err) {
      toast.error(`Role not saved: ${reason(err)}`);
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    setBusy(true);
    try {
      await admin.deleteStaffRole(role.id, moveTo ? Number(moveTo) : null, token);
      toast.success(`${role.name} deleted`);
      onSaved();
    } catch (err) {
      toast.error(`Not deleted: ${reason(err)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={role ? `Role · ${role.name}` : "New role"} onClose={onClose} testId="role-editor">
      <div className="space-y-base">
        <label className="flex flex-col gap-tight text-meta font-medium">Name
          <Input value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))} data-testid="role-name" />
        </label>
        <label className="flex flex-col gap-tight text-meta font-medium">Works like
          <select className="h-10 rounded-md border bg-background px-snug text-body" value={draft.base_kind}
                  onChange={e => setDraft(d => ({ ...d, base_kind: e.target.value }))} data-testid="role-kind">
            {KINDS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
          <span className="text-micro text-muted-foreground font-normal">
            The till treats holders as this kind for orders and bills; the switches below decide the rest.
          </span>
        </label>
        <label className="flex flex-col gap-tight text-meta font-medium">What it is for
          <Input value={draft.description} maxLength={200} placeholder="e.g. Kitchen display for their stations"
                 onChange={e => setDraft(d => ({ ...d, description: e.target.value }))} />
        </label>
        <label className="flex items-start gap-snug text-body cursor-pointer">
          <input type="checkbox" className="w-4 h-4 mt-1" checked={draft.is_default}
                 onChange={() => setDraft(d => ({ ...d, is_default: !d.is_default }))} data-testid="role-is-default" />
          <span className="flex flex-col">
            <span>New {KIND_WORD[draft.base_kind].toLowerCase()}s start with this role</span>
            <span className="text-micro text-muted-foreground">
              {draft.is_default && currentDefault ? `Replaces ${currentDefault.name} as the default` :
                currentDefault ? `Now: ${currentDefault.name}` : "Without one, new staff get the plain kind"}
            </span>
            {(draft.adds.length > 0 || draft.removes.length > 0) && (
              <span className="text-micro text-muted-foreground" data-testid="role-default-grant-note">
                It changes permissions, so it is given only when someone who manages permissions adds the person;
                anyone else adding staff gives the plain kind.
              </span>
            )}
          </span>
        </label>
        <PermissionGroups catalog={catalog} on={on} toggle={toggle} note={note} testPrefix="role-perm" />
        <TelegramItems items={items} itemsError={itemsError} value={draft.telegram}
                       onChange={telegram => setDraft(d => ({ ...d, telegram }))} />
        <div className="flex flex-wrap items-center gap-snug pt-snug border-t">
          {role && (
            <div className="flex items-center gap-snug mr-auto">
              {role.holders > 0 && (
                <select className="h-9 rounded-md border bg-background px-snug text-meta" value={moveTo}
                        onChange={e => setMoveTo(e.target.value)} aria-label="Move holders to" data-testid="role-move-to">
                  <option value="">Move its {role.holders} holder(s) to…</option>
                  {roles.filter(r => r.id !== role.id).map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              )}
              <Button variant="destructive" size="sm" disabled={busy || (role.holders > 0 && !moveTo)} onClick={remove}
                      data-testid="role-delete"><Trash2 className="w-4 h-4" /> Delete</Button>
            </div>
          )}
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="action" disabled={busy || !draft.name.trim()} onClick={save} data-testid="role-save">
            {busy ? "Saving…" : "Save role"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default function RolesPanel({ catalog, rolePerms, onKindsChanged }) {
  const { token } = usePOS();
  const [roles, setRoles] = useState(null);
  const [items, setItems] = useState(null);
  const [itemsError, setItemsError] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => {
    admin.listStaffRoles(token).then(r => { setRoles(r); setError(null); })
      .catch(err => setError(`Roles not loaded: ${reason(err)}`));
  }, [token]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    tg.telegramItems(token).then(setItems).catch(err => setItemsError(reason(err)));
  }, [token]);

  if (error) return <p className="text-meta text-danger" role="alert">{error}</p>;
  if (!roles) return <p className="text-meta text-muted-foreground">Loading roles…</p>;
  if (!catalog.length) {
    // The permission catalogue is refused without permissions.manage: no switch here
    // could save, so none is shown.
    return (
      <p className="text-meta text-muted-foreground" data-testid="roles-forbidden">
        Changing roles needs the "Manage permissions" right.
      </p>
    );
  }
  return (
    <div className="space-y-base" data-testid="roles-panel">
      <section className="space-y-snug">
        <div className="flex items-start gap-base">
          <p className="text-meta text-muted-foreground flex-1">
            A role is a set of permissions and Telegram items with a name. Give it to
            a person on their screen under Staff accounts. Changing a role changes everyone who holds it; a person's own
            switches still win. Owners can customize any role's name, permissions, and default assignments.
          </p>
          <Button variant="action" onClick={() => setEditing("new")} data-testid="role-new"><Plus className="w-4 h-4" /> New role</Button>
        </div>
        <div className="divide-y rounded-lg border bg-card">
          {roles.map(r => (
            <button key={r.id} type="button" onClick={() => setEditing(r)} data-testid={`role-row-${r.id}`}
                    className="w-full text-left px-base py-snug hover:bg-muted flex items-center gap-base">
              <span className="flex-1 min-w-0">
                <span className="text-body font-semibold">{r.name}</span>
                <span className="text-meta text-muted-foreground"> · works like {r.base_kind}</span>
                {r.is_default && (
                  <span className="ml-snug text-micro font-medium text-info" data-testid={`role-default-${r.id}`}>
                    new {r.base_kind}s start here
                  </span>
                )}
                {r.description && <span className="block text-meta text-muted-foreground truncate">{r.description}</span>}
              </span>
              <span className="text-meta text-muted-foreground tabular-nums">
                {r.holders} {r.holders === 1 ? "person" : "people"}
              </span>
            </button>
          ))}
        </div>
      </section>
      {editing && (
        <RoleEditor role={editing === "new" ? null : editing} roles={roles} catalog={catalog} rolePerms={rolePerms}
                    items={items} itemsError={itemsError}
                    onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}
